import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { IconAlert, IconCheck, IconChevronLeft, IconChevronRight, IconClose, IconSearch } from './icons';

/* ═══════════════════════════ أساسيات ═══════════════════════════ */

export function Spinner({ className = '' }: { className?: string }): JSX.Element {
  return (
    <span
      className={`inline-block h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
      role="status"
      aria-label="جارٍ التحميل"
    />
  );
}

export function LoadingBlock({ label = 'جارٍ التحميل…' }: { label?: string }): JSX.Element {
  return (
    <div className="flex items-center justify-center gap-3 py-14 text-slate-500 dark:text-slate-400">
      <Spinner />
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
}

export function EmptyState({
  title = 'لا توجد بيانات',
  description,
  action,
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
      <div className="rounded-full bg-slate-100 p-3 text-slate-400 dark:bg-slate-800 dark:text-slate-500">
        <IconAlert width={26} height={26} />
      </div>
      <p className="text-base font-semibold text-slate-700 dark:text-slate-200">{title}</p>
      {description ? <p className="max-w-md text-sm text-slate-500 dark:text-slate-400">{description}</p> : null}
      {action}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <div className="rounded-full bg-rose-50 p-3 text-rose-600 dark:bg-rose-950 dark:text-rose-400">
        <IconAlert width={26} height={26} />
      </div>
      <p className="text-base font-semibold text-rose-700 dark:text-rose-300">تعذّر تحميل البيانات</p>
      <p className="max-w-md text-sm text-slate-600 dark:text-slate-400">{message}</p>
      {onRetry ? (
        <button type="button" className="btn-secondary" onClick={onRetry}>
          إعادة المحاولة
        </button>
      ) : null}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}): JSX.Element {
  return (
    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 className="text-xl font-bold text-slate-900 dark:text-white sm:text-2xl">{title}</h1>
        {subtitle ? <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">{subtitle}</div> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function Card({
  title,
  subtitle,
  actions,
  children,
  className = '',
  bodyClassName = 'card-pad',
}: {
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}): JSX.Element {
  return (
    <section className={`card ${className}`}>
      {title || actions ? (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 dark:border-slate-800 sm:px-5">
          <div>
            <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100">{title}</h2>
            {subtitle ? <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{subtitle}</p> : null}
          </div>
          {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
        </header>
      ) : null}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

/* ═══════════════════════════ شارات وحالات ═══════════════════════════ */

type Tone = 'gray' | 'green' | 'red' | 'amber' | 'blue' | 'brand' | 'purple';

const TONES: Record<Tone, string> = {
  gray: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  green: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  red: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300',
  amber: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300',
  blue: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300',
  brand: 'bg-brand-100 text-brand-900 dark:bg-brand-950 dark:text-brand-200',
  purple: 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300',
};

export function Badge({ children, tone = 'gray' }: { children: ReactNode; tone?: Tone }): JSX.Element {
  return <span className={`badge ${TONES[tone]}`}>{children}</span>;
}

const STATUS_TONES: Record<string, Tone> = {
  active: 'green',
  leave: 'amber',
  terminated: 'red',
  maintenance: 'amber',
  retired: 'gray',
  draft: 'gray',
  approved: 'blue',
  paid: 'green',
  archived: 'purple',
  open: 'green',
  closing: 'amber',
  closed: 'purple',
  earning: 'green',
  deduction: 'red',
  owned: 'blue',
  leased: 'purple',
  company: 'green',
  deducted: 'amber',
  none: 'gray',
  cash: 'green',
  bank: 'blue',
  card: 'purple',
  credit: 'amber',
};

export function StatusBadge({ value, label }: { value: string; label?: string }): JSX.Element {
  return <Badge tone={STATUS_TONES[value] ?? 'gray'}>{label ?? value}</Badge>;
}

/* ═══════════════════════════ حقول الإدخال ═══════════════════════════ */

export function Field({
  label,
  error,
  hint,
  required,
  children,
  className = '',
}: {
  label?: string;
  error?: string | null;
  hint?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div className={className}>
      {label ? (
        <label className="label">
          {label}
          {required ? <span className="text-rose-600"> *</span> : null}
        </label>
      ) : null}
      {children}
      {hint && !error ? <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</p> : null}
      {error ? <p className="mt-1 text-xs font-semibold text-rose-600 dark:text-rose-400">{error}</p> : null}
    </div>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder = 'بحث…',
  className = '',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}): JSX.Element {
  return (
    <div className={`relative ${className}`}>
      <IconSearch className="pointer-events-none absolute inset-y-0 start-3 my-auto text-slate-400" width={18} height={18} />
      <input
        type="search"
        className="input ps-10"
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
}): JSX.Element {
  const id = useId();
  return (
    <label htmlFor={id} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
      <input
        id={id}
        type="checkbox"
        className="h-4 w-4 rounded border-slate-300 text-brand-700 focus:ring-brand-600 dark:border-slate-600 dark:bg-slate-800"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      {label}
    </label>
  );
}

/* ═══════════════════════════ مودال ═══════════════════════════ */

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const widths = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-4xl', xl: 'max-w-6xl' } as const;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <div
        className={`flex max-h-[95vh] w-full ${widths[size]} flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl dark:bg-slate-900 sm:rounded-2xl`}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-center justify-between gap-4 border-b border-slate-200 px-4 py-3 dark:border-slate-800 sm:px-5">
          <h2 className="text-base font-bold text-slate-900 dark:text-white">{title}</h2>
          <button type="button" className="btn-ghost !p-2" onClick={onClose} aria-label="إغلاق">
            <IconClose width={18} height={18} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">{children}</div>
        {footer ? (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-4 py-3 dark:border-slate-800 sm:px-5">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'تأكيد',
  tone = 'primary',
  onConfirm,
  onCancel,
  busy = false,
  children,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  tone?: 'primary' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
  children?: ReactNode;
}): JSX.Element | null {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
            إلغاء
          </button>
          <button
            type="button"
            className={tone === 'danger' ? 'btn-danger' : 'btn-primary'}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? <Spinner /> : null}
            {confirmLabel}
          </button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-slate-700 dark:text-slate-200">
        <div>{message}</div>
        {children}
      </div>
    </Modal>
  );
}

/* ═══════════════════════════ جدول ═══════════════════════════ */

export interface Column<T> {
  key: string;
  header: string;
  align?: 'start' | 'end' | 'center';
  numeric?: boolean;
  className?: string;
  render?: (row: T, index: number) => ReactNode;
}

export function DataTable<T extends Record<string, unknown>>({
  columns,
  rows,
  loading,
  emptyTitle,
  emptyDescription,
  footer,
  rowKey,
  onRowClick,
  dense = false,
}: {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  footer?: ReactNode;
  rowKey?: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;
  dense?: boolean;
}): JSX.Element {
  if (loading) return <LoadingBlock />;
  if (rows.length === 0) return <EmptyState title={emptyTitle} description={emptyDescription} />;

  const alignClass = (align?: string) => (align === 'end' ? 'text-end' : align === 'center' ? 'text-center' : 'text-start');

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
        <thead className="bg-slate-50 dark:bg-slate-800/60">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={`th ${alignClass(column.align)} ${column.numeric ? 'num' : ''} ${column.className ?? ''}`}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((row, index) => (
            <tr
              key={rowKey?.(row, index) ?? index}
              className={`${onRowClick ? 'cursor-pointer' : ''} transition hover:bg-slate-50 dark:hover:bg-slate-800/50`}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={`td ${alignClass(column.align)} ${column.numeric ? 'num' : ''} ${
                    dense ? '!py-2' : ''
                  } ${column.className ?? ''}`}
                >
                  {column.render ? column.render(row, index) : ((row[column.key] ?? '—') as ReactNode)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer ? <tfoot className="bg-slate-50 font-bold dark:bg-slate-800/60">{footer}</tfoot> : null}
      </table>
    </div>
  );
}

export function Pagination({
  page,
  pageCount,
  total,
  onChange,
}: {
  page: number;
  pageCount: number;
  total: number;
  onChange: (page: number) => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 text-sm dark:border-slate-800">
      <span className="text-slate-500 dark:text-slate-400">
        الإجمالي: <span className="num font-semibold">{total}</span>
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn-secondary !px-3 !py-1.5"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          aria-label="السابق"
        >
          <IconChevronRight width={16} height={16} />
        </button>
        <span className="num text-slate-600 dark:text-slate-300">
          {page} / {Math.max(1, pageCount)}
        </span>
        <button
          type="button"
          className="btn-secondary !px-3 !py-1.5"
          disabled={page >= pageCount}
          onClick={() => onChange(page + 1)}
          aria-label="التالي"
        >
          <IconChevronLeft width={16} height={16} />
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════ مؤشرات وبطاقات ═══════════════════════════ */

export function KpiCard({
  label,
  value,
  hint,
  tone = 'brand',
  icon,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
  icon?: ReactNode;
}): JSX.Element {
  return (
    <div className="card card-pad">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold text-slate-500 dark:text-slate-400">{label}</p>
          <p className="num mt-2 truncate text-xl font-bold text-slate-900 dark:text-white sm:text-2xl">{value}</p>
          {hint ? <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</p> : null}
        </div>
        {icon ? <div className={`rounded-lg p-2 ${TONES[tone]}`}>{icon}</div> : null}
      </div>
    </div>
  );
}

export function ProgressBar({
  value,
  tone = 'bg-brand-600',
  height = 'h-2',
}: {
  value: number;
  tone?: string;
  height?: string;
}): JSX.Element {
  const safe = Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0));
  return (
    <div className={`w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700 ${height}`}>
      <div className={`${height} rounded-full ${tone} transition-all`} style={{ width: `${safe}%` }} />
    </div>
  );
}

/** رسم أعمدة بسيط بالـ SVG (بلا مكتبات، ويدعم RTL) */
export function BarChart({
  data,
  height = 200,
  valueFormatter,
}: {
  data: Array<{ label: string; value: number; tone?: string }>;
  height?: number;
  valueFormatter?: (value: number) => string;
}): JSX.Element {
  const max = Math.max(1, ...data.map((d) => Math.abs(d.value)));
  if (data.length === 0) return <EmptyState title="لا توجد بيانات للرسم" />;
  return (
    <div className="flex items-end justify-between gap-2 overflow-x-auto" style={{ height }}>
      {data.map((item) => {
        const percent = (Math.abs(item.value) / max) * 100;
        return (
          <div key={item.label} className="flex min-w-[42px] flex-1 flex-col items-center justify-end gap-2">
            <span className="num text-[10px] font-semibold text-slate-600 dark:text-slate-300">
              {valueFormatter ? valueFormatter(item.value) : item.value.toLocaleString('en-US')}
            </span>
            <div
              className={`w-full rounded-t-md ${item.tone ?? 'bg-brand-600'}`}
              style={{ height: `${Math.max(2, percent)}%` }}
              title={`${item.label}: ${item.value}`}
            />
            <span className="truncate text-[10px] text-slate-500 dark:text-slate-400">{item.label}</span>
          </div>
        );
      })}
    </div>
  );
}

export function DonutChart({
  data,
  size = 180,
}: {
  data: Array<{ label: string; value: number; color: string }>;
  size?: number;
}): JSX.Element {
  const total = data.reduce((acc, d) => acc + Math.max(0, d.value), 0);
  const radius = size / 2 - 14;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  if (total <= 0) return <EmptyState title="لا توجد بيانات للتوزيع" />;

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:justify-around">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" strokeWidth={22} className="stroke-slate-200 dark:stroke-slate-800" />
        {data.map((item) => {
          const fraction = Math.max(0, item.value) / total;
          const dash = fraction * circumference;
          const element = (
            <circle
              key={item.label}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              strokeWidth={22}
              stroke={item.color}
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset}
            />
          );
          offset += dash;
          return element;
        })}
      </svg>
      <ul className="w-full max-w-xs space-y-2">
        {data.map((item) => (
          <li key={item.label} className="flex items-center justify-between gap-3 text-sm">
            <span className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
              <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: item.color }} />
              {item.label}
            </span>
            <span className="num text-slate-600 dark:text-slate-300">
              {((item.value / total) * 100).toFixed(1)}٪
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ═══════════════════════════ اختيار الشهر ═══════════════════════════ */

export function MonthNav({
  value,
  onChange,
  label,
  disabledNext = false,
}: {
  value: string;
  onChange: (month: string) => void;
  label: (month: string) => string;
  disabledNext?: boolean;
}): JSX.Element {
  const shift = (delta: number) => {
    const [year, month] = value.split('-').map(Number) as [number, number];
    const total = year * 12 + (month - 1) + delta;
    onChange(`${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`);
  };
  return (
    <div className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white p-1 dark:border-slate-700 dark:bg-slate-900">
      <button type="button" className="btn-ghost !px-2 !py-1.5" onClick={() => shift(-1)} aria-label="الشهر السابق">
        <IconChevronRight width={16} height={16} />
      </button>
      <span className="min-w-[7.5rem] text-center text-sm font-semibold text-slate-800 dark:text-slate-100">
        {label(value)}
      </span>
      <button
        type="button"
        className="btn-ghost !px-2 !py-1.5"
        onClick={() => shift(1)}
        disabled={disabledNext}
        aria-label="الشهر التالي"
      >
        <IconChevronLeft width={16} height={16} />
      </button>
    </div>
  );
}

/* ═══════════════════════════ تنبيهات (Toast) ═══════════════════════════ */

interface ToastItem {
  id: number;
  tone: 'success' | 'error' | 'info';
  message: string;
}

interface ToastContextValue {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [items, setItems] = useState<ToastItem[]>([]);

  const push = useCallback((tone: ToastItem['tone'], message: string) => {
    const id = Date.now() + Math.random();
    setItems((current) => [...current, { id, tone, message }]);
    setTimeout(() => setItems((current) => current.filter((item) => item.id !== id)), 5000);
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      success: (message: string) => push('success', message),
      error: (message: string) => push('error', message),
      info: (message: string) => push('info', message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed inset-x-0 bottom-20 z-[60] flex flex-col items-center gap-2 px-4 sm:bottom-6">
          {items.map((item) => (
            <div
              key={item.id}
              className={`pointer-events-auto flex w-full max-w-md items-start gap-2 rounded-lg px-4 py-3 text-sm font-medium shadow-lg ${
                item.tone === 'success'
                  ? 'bg-emerald-600 text-white'
                  : item.tone === 'error'
                    ? 'bg-rose-600 text-white'
                    : 'bg-slate-800 text-white dark:bg-slate-700'
              }`}
              role="status"
            >
              {item.tone === 'success' ? (
                <IconCheck width={18} height={18} className="mt-0.5 shrink-0" />
              ) : (
                <IconAlert width={18} height={18} className="mt-0.5 shrink-0" />
              )}
              <span className="whitespace-pre-line">{item.message}</span>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast يجب أن يُستخدم داخل ToastProvider');
  return context;
}

/**
 * يُعلِم المستخدم باسم الحقل حين يمنع المتصفح الحفظ (حقل إلزامي فارغ أو صيغة خاطئة).
 * بدونه تظهر فقاعة المتصفح بجانب الحقل — وقد يكون خارج الشاشة داخل النافذة ⇒ «الحفظ لا يعمل».
 * الاستخدام: <form onInvalidCapture={notifyInvalid} …>
 */
export function useInvalidFieldNotice(): (event: FormEvent<HTMLFormElement>) => void {
  const toast = useToast();
  const lastRef = useRef(0);
  return useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      const now = Date.now();
      if (now - lastRef.current < 1000) return; // تنبيه واحد لكل محاولة حفظ
      lastRef.current = now;
      const field = event.target as HTMLInputElement;
      let node: HTMLElement | null = field.parentElement;
      let label = '';
      while (node && !label) {
        label = node.querySelector(':scope > label.label')?.textContent?.replace('*', '').trim() ?? '';
        node = node.parentElement;
      }
      const reason = field.validity?.valueMissing ? 'حقل مطلوب' : 'صيغة غير صحيحة';
      toast.error(`لم يُحفظ — ${label ? `«${label}»: ` : ''}${reason}`);
      field.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    },
    [toast],
  );
}

/** شريط تبديل الوضع الليلي */
export function useTheme(): { dark: boolean; toggle: () => void } {
  const [dark, setDark] = useState<boolean>(() => {
    const stored = localStorage.getItem('sarha-theme');
    if (stored) return stored === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('sarha-theme', dark ? 'dark' : 'light');
  }, [dark]);

  return { dark, toggle: () => setDark((value) => !value) };
}
